import {
  useContext,
  ref,
  reactive,
  computed,
  readonly,
  InjectionKey,
  provide,
  inject,
} from '@nuxtjs/composition-api'
import { useLoading } from './ui/useLoading'
import { useLocation } from './useLocation'
// import { useConfig } from './useConfig'
import { useAddress } from './customer/useAddress'
import { useNotification } from './ui/useNotification'
import { FilterConfig, FilterOption, Store, StoreParams, PickupLocation } from '~/types/store'
import { Coordinate } from '~/types/geo'
import {
  getCurrentTimeString,
  getRegularTime,
  getTodayRegularTime,
  isAfterTime,
  isWithinTime,
} from '~/lib/utilities/datetime'
import { isPostalCode } from '~/lib/utilities/regex'
import { ReactiveValue } from '~/types/common'

export enum STORE_MAKERS {
  Normal = require('@/assets/images/icons/marker.png'),
  Active = require('@/assets/images/icons/marker-active.png'),
}

export const DEFAULT_FILTER: FilterConfig = {
  [FilterOption.Opened]: false,
  [FilterOption.AfterDusk]: false,
  [FilterOption.Sunday]: false,
}

const DUSK_TIME = '17:00'

const fullStores = ref<Store[]>([])

export const useStoreList = () => {
  const { app } = useContext()
  const { getCurrentCoordinate, checkPermissionState } = useLocation()
  const { logisticAddress, ensureAddresses } = useAddress()
  const { show: showNotification } = useNotification()
  // const { config } = useConfig()

  const loading = useLoading()
  const keyword = ref('')
  const stores = ref<Store[]>([])
  const pickupLocations = ref<PickupLocation[]>([])
  const initialPickupLocations = ref<PickupLocation[]>([])

  // prePostalCode stores are based on customer address's postalCode
  const prePostalCodeStores = ref<Store[]>([])
  const initialStores = ref<Store[]>([])
  const filterConfig = reactive(DEFAULT_FILTER)

  /* --- get stores --- */

  const getStoresByPostalCode = async (postalCode: string, payload?: StoreParams) => {
    const coordinate = await app.$api.location.getCoordinateFromPostcode(
      postalCode
    )
    const responseData = await getStoresByCoordinate(coordinate, payload)
    return responseData
  }

  const getStoresByCity = async (city: string, payload?: StoreParams) => {
    const coordinate = await app.$api.location.getCoordinateFromAddress(city)
    const responseData = await getStoresByCoordinate(coordinate, payload)
    return responseData
  }

  const getStoresByCoordinate = async (coordinate: Coordinate | null, payload?: StoreParams) => {
    let responseData: Store[] = []
    if (coordinate) {
      responseData = await app.$api.store.getStores(payload, coordinate)
    } else {
      responseData = await ensureFullStores()
    }

    return responseData
  }

  const getStoresByCurrentLocation = async (payload?: StoreParams) => {
    let coordinate: Coordinate | null = null

    try {
      coordinate = await getCurrentCoordinate()
    } catch (error: any) {
      coordinate = null
      if (
        error?.code !== undefined &&
        error?.PERMISSION_DENIED !== undefined &&
        error.code === error.PERMISSION_DENIED
      ) {
        showNotification({
          template: `<span>${app.i18n.t('common.store.noLocationAuth')}</span>`,
          duration: 6000,
          fixed: true,
        })
      }
    }

    if (!coordinate) return []

    return await getStoresByCoordinate(coordinate, payload)
  }

  /* --- pickup location stores --- */
  const getPickupLocationsByPostalCode = async (postalCode: string) => {
    const responseData = await app.$api.shipping.getPickupLocationsByPostalCode(
      postalCode
    )
    return responseData
  }

  /* --- ensure stores --- */

  const ensurePrePostalCodeStores = async (payload?: StoreParams) => {
    if (!prePostalCodeStores.value.length) {
      const postalCode = await getPrePostalCode()
      if (postalCode) {
        prePostalCodeStores.value = await getStoresByPostalCode(
          postalCode,
          payload
        )
      }
    }
    return prePostalCodeStores.value
  }

  const ensureFullStores = async (payload?: StoreParams) => {
    fullStores.value = await app.$api.store.getStores(payload)
    return fullStores.value
  }

  const getPrePostalCode = async () => {
    await ensureAddresses()
    return logisticAddress.value?.postalCode ?? ''
  }

  const initializePickupLocations = (
    postalCode: string
  ) => {
    return loading.scope(async () => {
      const responsePickupLocations = await getPickupLocationsByPostalCode(postalCode)
      initialPickupLocations.value = responsePickupLocations
      pickupLocations.value = responsePickupLocations
    })
  }

  const initializeStores = (
    prefilledKeyword: boolean = false,
    payload?: StoreParams
  ) => {
    return loading.scope(async () => {
      if (prefilledKeyword) {
        keyword.value = await getPrePostalCode()
      }
      let responseStores = await ensurePrePostalCodeStores(payload)
      if (!responseStores.length) {
        /* if no customer address 'postalCode',
        get stores by current coordinate */
        const permissionState = await checkPermissionState()
        if (permissionState === 'granted') {
          responseStores = await getStoresByCurrentLocation(payload)
        }
      }
      if (!responseStores.length) {
        /* if permission state is no 'granted', get full stores */
        responseStores = await ensureFullStores(payload)
      }
      initialStores.value = responseStores
      stores.value = responseStores
    })
  }


  /* --- search stores --- */

  const searchStores = () => {
    
    return loading.scope(async () => {
      const val = keyword.value.trim().toLowerCase()
      
      if (val) {
        if (isPostalCode(val)) {
          stores.value = await getStoresByPostalCode(val)
        } else {
          stores.value = await getStoresByCity(val)
        }
      } else {
        resetStores()
      }
    })
  }

  const searchPickupLocations = () => {
    return loading.scope(async () => {
      const val = keyword.value.trim().toLowerCase()
      if (val) {
        if (isPostalCode(val)) {
          pickupLocations.value = await getPickupLocationsByPostalCode(val)
        } else {
          resetPickupLocations()
        }
      } else {
        resetPickupLocations()
      }
    })
  }

  const searchStoresByCurrentLocation = () => {
    return loading.scope(async () => {
      let responseStores = await getStoresByCurrentLocation()
      if (!responseStores.length) {
        // if the user doesn't agree to share location, get prePostalCode stores
        responseStores = await ensurePrePostalCodeStores()
      }
      if (!responseStores.length) {
        // if 'prePostalCodeStores' is an empty array, get full Stores
        responseStores = await ensureFullStores()
      }
      stores.value = responseStores
    })
  }

  // available stores
  // const hiddenStoreNamesSet = computed(
  //   () => new Set(config.value?.hideStoreNames ?? [])
  // )

  const availableStores = computed(() => {
    return stores.value
    // return stores.value.filter(
    //   (store) => !hiddenStoreNamesSet.value.has(store.title.toLowerCase())
    // )
  })

  // filtered stores
  const filteredStores = computed(() => {
    const shouldFilter = Object.values(filterConfig).includes(true)
    if (!shouldFilter) return availableStores.value

    const currentTime = getCurrentTimeString()
    return availableStores.value.filter((store) => {
      const regularTimes = store.openingTimes?.regularTimes
      if (!regularTimes?.length) return false
      const regularTime = getTodayRegularTime(regularTimes)
      let isValid = true

      if (filterConfig[FilterOption.AfterDusk]) {
        isValid = regularTime?.closeTime
          ? isAfterTime(regularTime.closeTime, DUSK_TIME)
          : false
      }

      if (filterConfig[FilterOption.Opened]) {
        isValid =
          regularTime?.openTime && regularTime?.closeTime
            ? isWithinTime(
              currentTime,
              regularTime.openTime,
              regularTime.closeTime
            )
            : false
      }

      if (filterConfig[FilterOption.Sunday]) {
        const sundayRegularTime = getRegularTime(0, regularTimes)
        isValid = !!sundayRegularTime?.openTime
      }

      return isValid
    })
  })

  const setFilterConfig = (config: FilterConfig) => {
    Object.assign(filterConfig, config)
  }

  const resetStores = () => {
    stores.value = initialStores.value
  }

  const resetPickupLocations = () => {
    pickupLocations.value = initialPickupLocations.value
  }

  return {
    loading: readonly(loading.value),
    keyword,
    stores: availableStores,
    pickupLocations,
    filteredStores,

    initializeStores,
    getPrePostalCode,
    searchPickupLocations,
    searchStores,
    searchStoresByCurrentLocation,
    setFilterConfig,
    resetStores,
    initializePickupLocations
  }
}

// provide storeList
const StoreListProviderKey: InjectionKey<ReturnType<typeof useStoreList>> =
  Symbol('Provider:StoreList')

export const provideStoreList = () => {
  const result = useStoreList()
  provide(StoreListProviderKey, result)
}

// inject storeList
export const injectStoreList = () => {
  const result = inject(StoreListProviderKey)
  if (!result) {
    throw new Error('store list provider not set')
  }
  return result
}

export const useStoreListMap = (
  stores: ReactiveValue<Store[]>,
  activeStoreId?: ReactiveValue<number>
) => {
  const manualCenter = ref<Coordinate | null>(null)
 
  // const zoom = computed(() => {
  //   return manualCenter.value ? 14 : 10
  // })
  const zoom = 11
  const locatableStores = computed(() => {
    return stores.value.filter(
      (store) =>
        store.location?.lat !== undefined && store.location.lng !== undefined
    )
  })

  const locatableStoresMap = computed(() => {
    return new Map(locatableStores.value.map((store) => [store.id, store]))
  })

  const markers = computed(() => {
    return locatableStores.value.map((store) => ({
      position: {
        lat: store.location!.lat,
        lng: store.location!.lng,
      },
      storeId: store.id,
      store,
      type: store.type,
      icon:
        store.id === activeStoreId?.value
          ? STORE_MAKERS.Active
          : STORE_MAKERS.Normal,
    }))
  })

  const coordinatesCenter = computed(() => {
    const initialCoordinate = {
      lat: 0,
      lng: 0,
    }
    const length = locatableStores.value.length

    if (!length) return initialCoordinate

    const coordinateSum = locatableStores.value.reduce(
      (prev, current) => ({
        lat: prev.lat + current.location!.lat,
        lng: prev.lng + current.location!.lng,
      }),
      initialCoordinate
    )

    return {
      lat: coordinateSum.lat / length,
      lng: coordinateSum.lng / length,
    }
  })

  const center = computed(() => {
    return manualCenter.value ?? coordinatesCenter.value
  })

  const setManualCenter = (coordinate: Coordinate | null) => {
    manualCenter.value = coordinate
  }

  const focusToCenter = (storeId: number) => {
    const matchedStore = locatableStoresMap.value.get(storeId)

    setManualCenter(
      matchedStore
        ? {
          lat: matchedStore.location!.lat,
          lng: matchedStore.location!.lng,
        }
        : null
    )
  }

  return {
    zoom,
    center,
    markers,

    focusToCenter,
  }
}
